/**
 * SUSI — <gallery-viewer>
 *
 * Full-screen image viewer with zoom. The media gallery clones it out of a <template> into <body>.
 *
 * Children: [data-viewer-stage], [data-viewer-thumb][data-media-id][data-zoom-url],
 *           [data-viewer-prev], [data-viewer-next], [data-viewer-close]
 * API:      open(mediaId?, opener?), close(), next(), prev(), isOpen
 * Events:   on:gallery-viewer:select { mediaId } when it closes (the image last looked at)
 *
 * Pointer:  mouse — click to zoom in / out, move to pan, wheel to zoom
 *           touch — pinch to zoom, drag to pan, double tap to zoom, swipe sideways to change image
 * Keyboard: arrows change image, Escape closes
 */
(() => {
  if (customElements.get('gallery-viewer')) return;

  const theme = window.theme;
  const SWIPE_DISTANCE = 50;
  const DOUBLE_TAP_MS = 320;
  const MAX_SCALE = 6;
  const TAP_SCALE = 3;

  class GalleryViewer extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.stage = this.querySelector('[data-viewer-stage]');
      this.pointers = new Map();
      this.scale = 1;
      this.maxScale = 1;
      this.x = 0;
      this.y = 0;
      this.lastTap = 0;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.stage.addEventListener('pointercancel', (event) => this.onPointerUp(event, true));
      this.stage.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.stage);
    }

    disconnectedCallback() {
      if (this.isOpen) {
        theme.unlockScroll();
        theme.releaseFocus();
        this.isOpen = false;
      }
    }

    get thumbs() {
      return Array.from(this.querySelectorAll('[data-viewer-thumb]:not([hidden])'));
    }

    /**
     * @param {?string} mediaId - Image to start on (default: the first one).
     * @param {Element} [opener] - Receives focus again after closing.
     */
    open(mediaId, opener) {
      const thumbs = this.thumbs;
      if (!thumbs.length) return;
      this.opener = opener || null;

      if (!this.isOpen) {
        this.isOpen = true;
        this.hidden = false;
        theme.lockScroll();
        // Next frame so the opening transition runs
        requestAnimationFrame(() => this.classList.add('is-open'));
        theme.trapFocus(this, this.querySelector('[data-viewer-close]') || this);
      }

      const start = thumbs.find((thumb) => thumb.dataset.mediaId === String(mediaId)) || thumbs[0];
      this.show(start);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.hidden = true;
      theme.unlockScroll();
      theme.releaseFocus(this.opener);
      if (this.current) theme.emit('on:gallery-viewer:select', { mediaId: this.current.dataset.mediaId }, this);
      this.stage.replaceChildren();
      this.image = null;
    }

    next() {
      this.step(1);
    }

    prev() {
      this.step(-1);
    }

    step(direction) {
      const thumbs = this.thumbs;
      if (thumbs.length < 2) return;
      const index = thumbs.indexOf(this.current);
      this.show(thumbs[(index + direction + thumbs.length) % thumbs.length]);
    }

    show(thumb) {
      if (!thumb) return;
      this.current = thumb;
      this.querySelectorAll('[data-viewer-thumb]').forEach((other) => {
        if (other === thumb) other.setAttribute('aria-current', 'true');
        else other.removeAttribute('aria-current');
      });
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });

      const image = new Image();
      image.className = 'gallery-viewer__image';
      image.alt = '';
      image.draggable = false;
      this.image = image;
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.stage.classList.add('is-loading');
      this.classList.remove('is-zoomable', 'is-zoomed');

      image.addEventListener(
        'load',
        () => {
          if (this.image !== image) return;
          this.stage.classList.remove('is-loading');
          this.measure();
        },
        { once: true }
      );
      image.src = thumb.dataset.zoomUrl;
      this.stage.replaceChildren(image);
    }

    /* -- Geometry ------------------------------------------------------------ */

    /** Works out how far the image may be magnified and re-clamps the current view. */
    measure() {
      const image = this.image;
      if (!image || !image.naturalWidth || !image.offsetWidth) return;
      this.maxScale = Math.min(MAX_SCALE, Math.max(1, image.naturalWidth / image.offsetWidth));
      this.classList.toggle('is-zoomable', this.maxScale > 1.05);
      this.setScale(Math.min(this.scale, this.maxScale));
    }

    limits() {
      const image = this.image;
      return {
        x: Math.max(0, (image.offsetWidth * this.scale - this.stage.clientWidth) / 2),
        y: Math.max(0, (image.offsetHeight * this.scale - this.stage.clientHeight) / 2)
      };
    }

    setScale(value) {
      if (!this.image) return;
      this.scale = Math.min(this.maxScale, Math.max(1, value));
      this.render();
    }

    render() {
      if (!this.image) return;
      const limit = this.limits();
      this.x = Math.min(limit.x, Math.max(-limit.x, this.x));
      this.y = Math.min(limit.y, Math.max(-limit.y, this.y));
      this.image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.classList.toggle('is-zoomed', this.scale > 1.01);
    }

    /** Positions a zoomed image so the part under the pointer comes into view. */
    panTowards(event) {
      const box = this.stage.getBoundingClientRect();
      const limit = this.limits();
      const fromCentreX = (event.clientX - box.left) / box.width - 0.5;
      const fromCentreY = (event.clientY - box.top) / box.height - 0.5;
      this.x = -fromCentreX * 2 * limit.x;
      this.y = -fromCentreY * 2 * limit.y;
      this.render();
    }

    toggleZoom(event) {
      if (this.maxScale <= 1.05) return;
      if (this.scale > 1.01) {
        this.x = 0;
        this.y = 0;
        this.setScale(1);
      } else {
        // A tap / click goes to a comfortable magnification; pinch and wheel can go further
        this.setScale(Math.min(this.maxScale, TAP_SCALE));
        this.panTowards(event);
      }
    }

    /* -- Input --------------------------------------------------------------- */

    onClick(event) {
      const thumb = event.target.closest('[data-viewer-thumb]');
      if (thumb) {
        this.show(thumb);
      } else if (event.target.closest('[data-viewer-prev]')) {
        this.prev();
      } else if (event.target.closest('[data-viewer-next]')) {
        this.next();
      } else if (event.target.closest('[data-viewer-close]')) {
        this.close();
      }
    }

    onKeydown(event) {
      const forward = theme.isRTL() ? 'ArrowLeft' : 'ArrowRight';
      const back = theme.isRTL() ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === forward) {
        event.preventDefault();
        this.next();
      } else if (event.key === back) {
        event.preventDefault();
        this.prev();
      }
    }

    pinchDistance() {
      const [first, second] = Array.from(this.pointers.values());
      return Math.hypot(first.x - second.x, first.y - second.y);
    }

    onPointerDown(event) {
      if (!this.image || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 1) {
        this.gesture = { startX: event.clientX, startY: event.clientY, moved: false };
      } else if (this.pointers.size === 2) {
        this.gesture.moved = true;
        this.pinch = { distance: this.pinchDistance(), scale: this.scale };
      }
    }

    onPointerMove(event) {
      if (!this.image) return;
      const previous = this.pointers.get(event.pointerId);

      // Hovering mouse: the zoomed image follows the pointer
      if (!previous) {
        if (event.pointerType === 'mouse' && this.scale > 1.01) this.panTowards(event);
        return;
      }

      const deltaX = event.clientX - previous.x;
      const deltaY = event.clientY - previous.y;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinch) {
        this.setScale((this.pinch.scale * this.pinchDistance()) / this.pinch.distance);
        return;
      }

      if (Math.hypot(event.clientX - this.gesture.startX, event.clientY - this.gesture.startY) > 8) this.gesture.moved = true;
      if (this.scale > 1.01 && event.pointerType !== 'mouse') {
        this.x += deltaX;
        this.y += deltaY;
        this.render();
      }
    }

    onPointerUp(event, cancelled) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
      if (this.pointers.size || cancelled || !this.gesture) return;

      const gesture = this.gesture;
      this.gesture = null;

      if (!gesture.moved) {
        if (event.pointerType === 'mouse') {
          this.toggleZoom(event);
        } else {
          const now = Date.now();
          if (now - this.lastTap < DOUBLE_TAP_MS) {
            this.toggleZoom(event);
            this.lastTap = 0;
          } else {
            this.lastTap = now;
          }
        }
        return;
      }

      // Sideways swipe on an image that is not magnified changes the image
      if (this.scale <= 1.01 && event.pointerType !== 'mouse') {
        const travelX = event.clientX - gesture.startX;
        const travelY = event.clientY - gesture.startY;
        if (Math.abs(travelX) > SWIPE_DISTANCE && Math.abs(travelX) > Math.abs(travelY) * 1.5) {
          const forward = theme.isRTL() ? travelX > 0 : travelX < 0;
          if (forward) this.next();
          else this.prev();
        }
      }
    }

    onWheel(event) {
      if (!this.image || this.maxScale <= 1.05) return;
      event.preventDefault();
      const before = this.scale;
      this.setScale(this.scale * Math.exp(-event.deltaY * 0.0015));
      if (before <= 1.01 && this.scale > 1.01) this.panTowards(event);
    }
  }

  customElements.define('gallery-viewer', GalleryViewer);
})();
